import React from 'react';
import classnames from 'classnames';
import {Heading, Text, ListGroupItem, IconButton} from '@albert-io/atomic';
import {ANNOTATION_SHIELD_EXEMPT_PROPS} from 'client/components/PracticeView/Annotations/shared/annotationFreeze';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import type {QuestionModelV3 as Question} from 'src/resources/augmented/Question/QuestionModel.v3';
import type {QuestionSetModelV1 as QuestionSet} from 'resources/augmented/QuestionSet/QuestionSetModel.v1';

interface Props {
  question: Question;
  questionSet: QuestionSet;
  navigateBackProps?: {disabled: boolean; handleNavigation: () => void};
  navigateNextProps?: {disabled: boolean; handleNavigation: () => void};
  headingActions?: any;
  modifyTitle?: (args: any) => void;
  showDifficulty: boolean;
  customHeader?: any;
  /** Rendered as the first child of the heading row, before the title. */
  leadingHeaderAction?: React.ReactNode;
  /**
   * Keep the title on one line, ellipsised. For layouts that give the header a fixed
   * height, where a wrapped title spills out of it instead of growing the row.
   */
  truncateTitle?: boolean;
}

const HeadingBar = ({
  question,
  questionSet,
  navigateBackProps,
  navigateNextProps,
  headingActions,
  modifyTitle,
  showDifficulty = true,
  customHeader = null,
  leadingHeaderAction,
  truncateTitle = false
}: Props) => {
  return (
    <ListGroupItem className='q-heading-block'>
      {leadingHeaderAction && <div className='mr-x2 flex'>{leadingHeaderAction}</div>}
      {!customHeader && (
        // min-w-x0: the heading is the flex-grow item, so without it a nowrap title sets
        // its min-content width and pushes the header actions off the row.
        <div className={classnames('q-heading-block__heading', {'min-w-x0': truncateTitle})}>
          <Heading
            as='h1'
            size='xs'
            // The markdown renderer nests the text a level down, and text-overflow only
            // draws on the box whose own inline content overflows.
            className={classnames('m-x0', {'truncate [&_*]:truncate': truncateTitle})}
          >
            {/* They key is added to force a rerender on question change.
           This is useful when translating. See `markdownRendererUtils.js` for more info. */}
            <MarkdownRendererV2
              key={question.getId()}
              text={modifyTitle ? modifyTitle({question, questionSet}) : question.getTitle()}
            />
          </Heading>
          {showDifficulty && <Text size='xs'>{question.getDifficultyLabel()}</Text>}
        </div>
      )}
      {customHeader && customHeader}

      {navigateBackProps && (
        <IconButton
          disabled={navigateBackProps.disabled}
          onClick={navigateBackProps.handleNavigation}
          variant='text'
          icon='chevron-left'
          aria-label='Go to previous question'
          aria-hidden={false}
          {...ANNOTATION_SHIELD_EXEMPT_PROPS}
        />
      )}
      {navigateNextProps && (
        <IconButton
          disabled={navigateNextProps.disabled}
          onClick={navigateNextProps.handleNavigation}
          variant='text'
          className='ml-x1'
          icon='chevron-right'
          aria-label='Go to next question'
          aria-hidden={false}
          {...ANNOTATION_SHIELD_EXEMPT_PROPS}
        />
      )}
      {headingActions && React.cloneElement(headingActions, {question})}
    </ListGroupItem>
  );
};

export {HeadingBar};
